import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

const SectionFormModal = ({ isOpen, onClose, onSave, section }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    title: "",
    footerImg: "",
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState("");
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (section) {
      setFormData({
        title: section.title || "",
        footerImg: section.footerImg || "",
      });
      setImagePreview(section.footerImg || "");
    } else {
      setFormData({
        title: "",
        footerImg: "",
      });
      setImagePreview("");
    }
    setErrors({});
    setImageError(false);
  }, [section, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Update image preview when footerImg changes
    if (name === "footerImg") {
      setImagePreview(value);
      setImageError(false);
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleClearImage = () => {
    setFormData((prev) => ({
      ...prev,
      footerImg: "",
    }));
    setImagePreview("");
    setImageError(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Section title is required";
    } else if (formData.title.trim().length < 3) {
      newErrors.title = "Section title must be at least 3 characters";
    }

    // Validate footerImg URL format if provided
    if (formData.footerImg.trim()) {
      try {
        new URL(formData.footerImg);
      } catch {
        newErrors.footerImg = "Please enter a valid URL";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        status: "error",
        duration: 3000,
      });
      return;
    }

    const dataToSave = {
      title: formData.title.trim(),
      footerImg: formData.footerImg.trim(),
    };

    onSave(dataToSave);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      title: "",
      footerImg: "",
    });
    setErrors({});
    setImagePreview("");
    setImageError(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            {section ? `Edit Section: ${section.title}` : "Add New Section"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired isInvalid={!!errors.title}>
                <FormLabel>Section Title</FormLabel>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Chronic Diseases"
                  autoFocus
                />
                {errors.title && (
                  <FormErrorMessage>{errors.title}</FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={!!errors.footerImg}>
                <FormLabel>Footer Image URL (Optional)</FormLabel>
                <Input
                  name="footerImg"
                  value={formData.footerImg}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                />
                {errors.footerImg && (
                  <FormErrorMessage>{errors.footerImg}</FormErrorMessage>
                )}

                {/* Image Preview */}
                {imagePreview && !imageError && (
                  <Box
                    mt={4}
                    position="relative"
                    borderWidth="1px"
                    borderRadius="md"
                    overflow="hidden"
                    p={2}
                  >
                    <IconButton
                      icon={<CloseIcon />}
                      size="sm"
                      colorScheme="red"
                      position="absolute"
                      top={2}
                      right={2}
                      onClick={handleClearImage}
                      aria-label="Remove image"
                      zIndex={1}
                    />
                    <Image
                      src={imagePreview}
                      alt="Footer preview"
                      maxH="200px"
                      mx="auto"
                      onError={handleImageError}
                    />
                  </Box>
                )}

                {/* Error Message for Invalid Image */}
                {imagePreview && imageError && (
                  <Box
                    mt={4}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="red.300"
                    bg="red.50"
                  >
                    <Text color="red.600" fontSize="sm">
                      Unable to load image. Please check the URL.
                    </Text>
                  </Box>
                )}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={handleClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" type="submit">
              {section ? "Update Section" : "Create Section"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default SectionFormModal;
